import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiRequest } from "@/hooks/use-api";
import { contractorScopeHeaders } from "@/lib/contractor-scope";
import type { SelectedProduct, Product } from "@/types/product";

const CART_QUERY_KEY = ["/api/cart"];
const DEBOUNCE_MS = 2000;

interface CartOrderResponse {
  uid: string;
  products: CartProductPayload[];
  [key: string]: any;
}

/** Shape of products stored in the cart draft on the server (OrderProduct format). */
interface CartProductPayload {
  productId: string;
  hajocaProductId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  quantityReceived?: number;
  notes?: string;
}

interface UseShoppingCartOptions {
  /** Seed products (e.g. from editOrder prop). When provided, cart persistence is disabled. */
  initialProducts?: SelectedProduct[];
  /** Disable cart sync entirely. Used in edit/view mode where the cart is an existing order. */
  disabled?: boolean;
}

/**
 * Convert a SelectedProduct to the OrderProduct shape stored in the cart draft.
 */
function toPayload(p: SelectedProduct): CartProductPayload {
  const unitPrice = p.unitPrice ?? p.currentPrice ?? 0;
  return {
    productId: p.uid,
    hajocaProductId: p.hajocaProductId,
    description: p.description,
    quantity: p.quantity,
    unitPrice,
    totalPrice: unitPrice * p.quantity,
    quantityReceived: 0,
    notes: p.notes || undefined,
  };
}

/**
 * Shopping cart hook. Replaces useOrderCart + useOrderAutoSave for the new order flow.
 *
 * Behavior:
 * - On mount (when not disabled), fetches the user's existing cart and populates
 *   `selectedProducts` with it. No cart exists → empty array.
 * - Any change to the cart is persisted to the server via PUT /api/cart, debounced 2s.
 * - `clearCart()` removes the cart on the server (DELETE /api/cart) and locally.
 * - After order submission, the server flips isCart=false; callers should call
 *   `invalidateCart()` to drop the local cart state so the next visit starts fresh.
 *
 * When `disabled` is true (edit/view modes), no server calls are made. The hook
 * just manages local state seeded from `initialProducts`.
 */
export function useShoppingCart({
  initialProducts,
  disabled = false,
}: UseShoppingCartOptions = {}) {
  const queryClient = useQueryClient();
  const { apiRequest } = useApiRequest();

  // Pin the contractor scope active when this hook mounted (empty for
  // contractor users). Every cart request uses the pinned scope so the
  // unmount flush of a debounced save can't write this cart into a different
  // contractor after a PC user switches contractors (which remounts the flow).
  const scopeHeadersRef = useRef(contractorScopeHeaders());

  // Load cart from server on mount (unless disabled or seeded).
  const cartQuery = useQuery({
    queryKey: CART_QUERY_KEY,
    queryFn: async (): Promise<CartOrderResponse | null> => {
      const response = await apiRequest(
        "GET",
        "/api/cart",
        undefined,
        scopeHeadersRef.current,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      return response.json();
    },
    enabled: !disabled && !initialProducts,
    staleTime: Infinity,
  });

  // Derive initial cart products: either from editOrder seed, or from loaded cart.
  // We keep these as full SelectedProduct objects (with price/description) by merging
  // loaded cart data with product details that callers have access to.
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    initialProducts ?? [],
  );

  // Track the cart order ID (used by submission to PATCH/submit the right order).
  const [cartOrderId, setCartOrderId] = useState<string | null>(null);

  // Hydrate local state from fetched cart (once loaded).
  // We only hydrate on the initial load — subsequent changes are driven by local state.
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (disabled || initialProducts) return;
    if (hasHydratedRef.current) return;
    if (!cartQuery.data) {
      if (cartQuery.isSuccess) {
        // No cart exists yet — mark hydrated so we can start saving on change.
        hasHydratedRef.current = true;
      }
      return;
    }

    setCartOrderId(cartQuery.data.uid);

    // Reconstruct SelectedProduct[] from the stored OrderProduct format.
    // The cart stores enough fields (productId, hajocaProductId, description,
    // quantity, unitPrice) to render without a separate product lookup.
    const products: SelectedProduct[] = (cartQuery.data.products || []).map(
      (p: CartProductPayload) =>
        ({
          uid: p.productId,
          hajocaProductId: p.hajocaProductId,
          description: p.description,
          quantity: p.quantity,
          unitPrice: p.unitPrice,
          notes: p.notes,
        }) as SelectedProduct,
    );
    setSelectedProducts(products);
    hasHydratedRef.current = true;
  }, [cartQuery.data, cartQuery.isSuccess, disabled, initialProducts]);

  // Mutations
  const putMutation = useMutation({
    mutationFn: async (products: SelectedProduct[]) => {
      const payload = products.map(toPayload);
      const response = await apiRequest(
        "PUT",
        "/api/cart",
        { products: payload },
        scopeHeadersRef.current,
      );
      if (!response.ok) {
        throw new Error(`Failed to save cart: ${response.status}`);
      }
      return response.json() as Promise<CartOrderResponse>;
    },
    onSuccess: (cart) => {
      setCartOrderId(cart.uid);
      // Update the query cache so refetches see the latest data without refetching.
      queryClient.setQueryData(CART_QUERY_KEY, cart);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "DELETE",
        "/api/cart",
        undefined,
        scopeHeadersRef.current,
      );
      if (!response.ok) {
        throw new Error(`Failed to delete cart: ${response.status}`);
      }
      return response.json();
    },
    onSuccess: () => {
      setCartOrderId(null);
      queryClient.setQueryData(CART_QUERY_KEY, null);
    },
  });

  // Debounced save on cart changes.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingProductsRef = useRef<SelectedProduct[] | null>(null);

  const scheduleSave = useCallback(
    (products: SelectedProduct[]) => {
      if (disabled || initialProducts) return;
      if (!hasHydratedRef.current) return; // Don't save before the initial load completes

      pendingProductsRef.current = products;

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        const toSave = pendingProductsRef.current;
        if (toSave === null) return;
        // Empty cart: delete it instead of sending an empty PUT (avoids stale empty rows)
        if (toSave.length === 0) {
          if (cartOrderId) {
            deleteMutation.mutate();
          }
        } else {
          putMutation.mutate(toSave);
        }
      }, DEBOUNCE_MS);
    },
    [disabled, initialProducts, cartOrderId, putMutation, deleteMutation],
  );

  // Flush pending save on unmount to avoid losing the last change.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        const toSave = pendingProductsRef.current;
        if (toSave && toSave.length > 0 && hasHydratedRef.current) {
          putMutation.mutate(toSave);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Cart operations ---

  const updateProducts = useCallback(
    (updater: (prev: SelectedProduct[]) => SelectedProduct[]) => {
      setSelectedProducts((prev) => {
        const next = updater(prev);
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const addProduct = useCallback(
    (product: Product, quantity: number) => {
      updateProducts((prev) => {
        const existingIndex = prev.findIndex((p) => p.uid === product.uid);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity: updated[existingIndex].quantity + quantity,
          };
          return updated;
        }
        return [
          ...prev,
          {
            ...product,
            quantity,
            unitPrice: product.currentPrice ?? 0,
          },
        ];
      });
    },
    [updateProducts],
  );

  const removeProduct = useCallback(
    (productId: string) => {
      updateProducts((prev) => prev.filter((p) => p.uid !== productId));
    },
    [updateProducts],
  );

  const changeQuantity = useCallback(
    (productId: string, quantity: number) => {
      if (quantity <= 0) {
        updateProducts((prev) => prev.filter((p) => p.uid !== productId));
        return;
      }
      updateProducts((prev) =>
        prev.map((p) => (p.uid === productId ? { ...p, quantity } : p)),
      );
    },
    [updateProducts],
  );

  const selectProduct = useCallback(
    (product: Product, quantity: number) => {
      if (quantity <= 0) {
        updateProducts((prev) => prev.filter((p) => p.uid !== product.uid));
        return;
      }
      updateProducts((prev) => {
        const existingIndex = prev.findIndex((p) => p.uid === product.uid);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity,
          };
          return updated;
        }
        return [
          ...prev,
          {
            ...product,
            quantity,
            unitPrice: product.currentPrice ?? 0,
          },
        ];
      });
    },
    [updateProducts],
  );

  const clearCart = useCallback(() => {
    // Cancel any pending debounced save
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    pendingProductsRef.current = null;
    setSelectedProducts([]);
    if (!disabled && !initialProducts && cartOrderId) {
      deleteMutation.mutate();
    }
  }, [disabled, initialProducts, cartOrderId, deleteMutation]);

  /** Replace entire cart with new products (e.g. applying a template). */
  const replaceProducts = useCallback(
    (products: SelectedProduct[]) => {
      setSelectedProducts(products);
      scheduleSave(products);
    },
    [scheduleSave],
  );

  /**
   * Reset the cart after submitting an order. The server already flipped isCart to
   * false on the submitted order, so there is nothing to DELETE — but the local state
   * and any pending debounced save must be dropped too. Otherwise the unmount flush
   * re-sends the submitted items and the server creates a brand new cart from them.
   */
  const invalidateCart = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    pendingProductsRef.current = null;
    setSelectedProducts([]);
    setCartOrderId(null);
    queryClient.setQueryData(CART_QUERY_KEY, null);
    queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
  }, [queryClient]);

  return {
    selectedProducts,
    setSelectedProducts: replaceProducts,
    cartOrderId,
    isLoading: !disabled && !initialProducts && cartQuery.isLoading,
    addProduct,
    removeProduct,
    changeQuantity,
    selectProduct,
    clearAll: clearCart,
    invalidateCart,
  };
}
